import React, { useEffect, useState } from "react";
import { api, apiError } from "@/lib/apiClient";
import { useStatuts } from "@/context/StatutsContext";
import { Plus, Loader2, Pencil, X, Power, Trash2, ArrowUp, ArrowDown, Tag, ListChecks } from "lucide-react";
import { toast } from "sonner";

const CAT_LABELS = {
  A_PLANIFIER: "À planifier", PLANIFIE: "Planifié", EN_COURS: "En cours",
  A_REPLANIFIER: "À replanifier", TERMINE: "Terminé", ANNULE: "Annulé",
};

export default function Parametrage() {
  const [tab, setTab] = useState("vt");
  return (
    <div className="space-y-5">
      <div>
        <h1 className="font-heading text-2xl font-extrabold tracking-tight text-slate-900">Paramétrage</h1>
        <p className="text-sm text-slate-500">Configurez dynamiquement les éléments du CRM. Ces paramètres sont utilisés partout dans l'application.</p>
      </div>
      <div className="flex gap-1 border-b border-slate-200">
        <TabBtn active={tab === "vt"} onClick={() => setTab("vt")} icon={Tag} testid="tab-vt">Types de VT</TabBtn>
        <TabBtn active={tab === "statuts"} onClick={() => setTab("statuts")} icon={ListChecks} testid="tab-statuts">Statuts des rendez-vous</TabBtn>
      </div>
      {tab === "vt" ? <VtTypesTab /> : <StatutsTab />}
    </div>
  );
}

function TabBtn({ active, onClick, icon: Icon, children, testid }) {
  return (
    <button data-testid={testid} onClick={onClick} className={`flex items-center gap-2 border-b-2 px-4 py-2.5 text-sm font-semibold transition-colors ${active ? "border-[#0A0A0A] text-slate-900" : "border-transparent text-slate-400 hover:text-slate-600"}`}>
      <Icon className="h-4 w-4" /> {children}
    </button>
  );
}

/* ------------------------- TYPES DE VT ------------------------- */
const EMPTY_VT = { nom: "", prix: 0, actif: true, description: "" };
function VtTypesTab() {
  const [rows, setRows] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_VT);

  const load = async () => setRows((await api.get("/vt-types?all=true")).data);
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(EMPTY_VT); setShowForm(true); };
  const openEdit = (t) => { setEditing(t); setForm({ nom: t.nom, prix: t.prix, actif: t.actif, description: t.description || "" }); setShowForm(true); };

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editing) await api.put(`/vt-types/${editing.id}`, form);
      else await api.post("/vt-types", form);
      toast.success(editing ? "Type de VT modifié" : "Type de VT créé");
      setShowForm(false); load();
    } catch (err) { toast.error(apiError(err)); }
  };
  const toggle = async (t) => { try { await api.put(`/vt-types/${t.id}`, { ...t, actif: !t.actif }); load(); } catch (err) { toast.error(apiError(err)); } };
  const remove = async (t) => {
    if (!window.confirm(`Supprimer définitivement le type « ${t.nom} » ?`)) return;
    try { await api.delete(`/vt-types/${t.id}`); toast.success("Type supprimé"); load(); }
    catch (err) { toast.error(apiError(err)); }
  };

  if (!rows) return <Loading />;
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-500">Le prix correspond au prix d'un <b>jeton</b> pour ce type de VT. Les achats déjà réalisés conservent leur prix.</p>
        <button data-testid="vt-add" onClick={openNew} className="flex items-center gap-2 rounded-lg bg-[#0A0A0A] px-3 py-2 text-sm font-semibold text-white hover:bg-slate-800"><Plus className="h-4 w-4" /> Ajouter</button>
      </div>
      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
        <table className="w-full text-left text-sm" data-testid="vt-table">
          <thead><tr className="border-b border-slate-200 bg-slate-50 text-[11px] uppercase tracking-wide text-slate-500">
            <th className="px-4 py-2.5">Type de VT</th><th className="px-4 py-2.5">Prix du jeton</th><th className="px-4 py-2.5">Statut</th><th className="px-4 py-2.5 text-right">Actions</th>
          </tr></thead>
          <tbody>
            {rows.map((t) => (
              <tr key={t.id} className="border-b border-slate-100 hover:bg-slate-50/60">
                <td className="px-4 py-2.5 font-medium text-slate-900">{t.nom}</td>
                <td className="px-4 py-2.5 mono">{t.prix != null ? `${Number(t.prix).toLocaleString("fr-FR")} €` : "—"}</td>
                <td className="px-4 py-2.5">{t.actif ? <span className="text-xs font-semibold text-emerald-600">Actif</span> : <span className="text-xs font-semibold text-slate-400">Inactif</span>}</td>
                <td className="px-4 py-2.5">
                  <div className="flex justify-end gap-1">
                    <button data-testid={`vt-edit-${t.id}`} onClick={() => openEdit(t)} className="rounded p-1.5 text-slate-500 hover:bg-slate-100" title="Modifier"><Pencil className="h-4 w-4" /></button>
                    <button onClick={() => toggle(t)} className="rounded p-1.5 text-slate-500 hover:bg-slate-100" title={t.actif ? "Désactiver" : "Activer"}><Power className="h-4 w-4" /></button>
                    <button data-testid={`vt-delete-${t.id}`} onClick={() => remove(t)} className="rounded p-1.5 text-rose-500 hover:bg-rose-50" title="Supprimer"><Trash2 className="h-4 w-4" /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {showForm && (
        <Modal title={`${editing ? "Modifier" : "Nouveau"} type de VT`} onClose={() => setShowForm(false)} onSubmit={save} testid="vt-form" submitId="vt-form-submit" submitLabel={editing ? "Enregistrer" : "Créer"}>
          <FieldRow label="Nom"><input data-testid="vt-form-nom" required value={form.nom} onChange={(e) => setForm({ ...form, nom: e.target.value })} className="pf-input" /></FieldRow>
          <FieldRow label="Prix du jeton (€)"><input data-testid="vt-form-prix" type="number" step="0.01" required value={form.prix} onChange={(e) => setForm({ ...form, prix: parseFloat(e.target.value) })} className="pf-input" /></FieldRow>
          <FieldRow label="Statut"><select value={form.actif ? "1" : "0"} onChange={(e) => setForm({ ...form, actif: e.target.value === "1" })} className="pf-input"><option value="1">Actif</option><option value="0">Inactif</option></select></FieldRow>
        </Modal>
      )}
    </div>
  );
}

/* ------------------------- STATUTS ------------------------- */
const EMPTY_ST = { nom: "", categorie: "A_PLANIFIER", color: "#64748b", actif: true };
function StatutsTab() {
  const { refresh: refreshGlobal } = useStatuts();
  const [rows, setRows] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_ST);

  const load = async () => setRows((await api.get("/statuts?all=true")).data);
  useEffect(() => { load(); }, []);
  const afterChange = async () => { await load(); refreshGlobal(); };

  const openNew = () => { setEditing(null); setForm(EMPTY_ST); setShowForm(true); };
  const openEdit = (s) => { setEditing(s); setForm({ nom: s.nom, categorie: s.categorie, color: s.color, actif: s.actif }); setShowForm(true); };

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editing) await api.put(`/statuts/${editing.id}`, form);
      else await api.post("/statuts", form);
      toast.success(editing ? "Statut modifié" : "Statut créé");
      setShowForm(false); afterChange();
    } catch (err) { toast.error(apiError(err)); }
  };
  const toggle = async (s) => { try { await api.put(`/statuts/${s.id}`, { actif: !s.actif }); afterChange(); } catch (err) { toast.error(apiError(err)); } };
  const remove = async (s) => {
    if (!window.confirm(`Supprimer définitivement le statut « ${s.nom} » ?`)) return;
    try { await api.delete(`/statuts/${s.id}`); toast.success("Statut supprimé"); afterChange(); }
    catch (err) { toast.error(apiError(err)); }
  };
  const move = async (idx, dir) => {
    const arr = [...rows];
    const j = idx + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[idx], arr[j]] = [arr[j], arr[idx]];
    setRows(arr);
    try { await api.patch("/statuts/reorder", { ordered_ids: arr.map((x) => x.id) }); refreshGlobal(); }
    catch (err) { toast.error(apiError(err)); load(); }
  };

  if (!rows) return <Loading />;
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-500">Chaque statut possède une <b>catégorie système</b> (utilisée par le dashboard/planning) et une <b>couleur</b> synchronisée partout.</p>
        <button data-testid="statut-add" onClick={openNew} className="flex items-center gap-2 rounded-lg bg-[#0A0A0A] px-3 py-2 text-sm font-semibold text-white hover:bg-slate-800"><Plus className="h-4 w-4" /> Ajouter</button>
      </div>
      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
        <table className="w-full text-left text-sm" data-testid="statut-table">
          <thead><tr className="border-b border-slate-200 bg-slate-50 text-[11px] uppercase tracking-wide text-slate-500">
            <th className="px-4 py-2.5">Ordre</th><th className="px-4 py-2.5">Statut</th><th className="px-4 py-2.5">Catégorie</th><th className="px-4 py-2.5">Couleur</th><th className="px-4 py-2.5">Actif</th><th className="px-4 py-2.5 text-right">Actions</th>
          </tr></thead>
          <tbody>
            {rows.map((s, idx) => (
              <tr key={s.id} className="border-b border-slate-100 hover:bg-slate-50/60">
                <td className="px-4 py-2.5">
                  <div className="flex items-center gap-1">
                    <span className="mono w-5 text-slate-500">{s.ordre}</span>
                    <button onClick={() => move(idx, -1)} disabled={idx === 0} className="rounded p-1 text-slate-400 hover:bg-slate-100 disabled:opacity-30"><ArrowUp className="h-3.5 w-3.5" /></button>
                    <button onClick={() => move(idx, 1)} disabled={idx === rows.length - 1} className="rounded p-1 text-slate-400 hover:bg-slate-100 disabled:opacity-30"><ArrowDown className="h-3.5 w-3.5" /></button>
                  </div>
                </td>
                <td className="px-4 py-2.5">
                  <span className="inline-flex items-center rounded-md px-2 py-0.5 text-[11px] font-bold uppercase tracking-wide" style={{ backgroundColor: `${s.color}1a`, color: s.color, border: `1px solid ${s.color}40` }}>{s.nom}</span>
                </td>
                <td className="px-4 py-2.5 text-slate-600">{CAT_LABELS[s.categorie] || s.categorie}</td>
                <td className="px-4 py-2.5"><span className="inline-block h-4 w-8 rounded" style={{ background: s.color }} /> <span className="mono text-xs text-slate-400">{s.color}</span></td>
                <td className="px-4 py-2.5">{s.actif ? <span className="text-xs font-semibold text-emerald-600">Actif</span> : <span className="text-xs font-semibold text-slate-400">Inactif</span>}</td>
                <td className="px-4 py-2.5">
                  <div className="flex justify-end gap-1">
                    <button data-testid={`statut-edit-${s.code}`} onClick={() => openEdit(s)} className="rounded p-1.5 text-slate-500 hover:bg-slate-100" title="Modifier"><Pencil className="h-4 w-4" /></button>
                    <button onClick={() => toggle(s)} className="rounded p-1.5 text-slate-500 hover:bg-slate-100" title={s.actif ? "Désactiver" : "Activer"}><Power className="h-4 w-4" /></button>
                    <button data-testid={`statut-delete-${s.code}`} onClick={() => remove(s)} className="rounded p-1.5 text-rose-500 hover:bg-rose-50" title="Supprimer"><Trash2 className="h-4 w-4" /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {showForm && (
        <Modal title={`${editing ? "Modifier" : "Nouveau"} statut`} onClose={() => setShowForm(false)} onSubmit={save} testid="statut-form" submitId="statut-form-submit" submitLabel={editing ? "Enregistrer" : "Créer"}>
          <FieldRow label="Nom"><input data-testid="statut-form-nom" required value={form.nom} onChange={(e) => setForm({ ...form, nom: e.target.value })} className="pf-input" /></FieldRow>
          <FieldRow label="Catégorie système">
            <select data-testid="statut-form-categorie" value={form.categorie} onChange={(e) => setForm({ ...form, categorie: e.target.value })} className="pf-input">
              {Object.entries(CAT_LABELS).map(([k, l]) => <option key={k} value={k}>{l} ({k})</option>)}
            </select>
          </FieldRow>
          <FieldRow label="Couleur">
            <div className="flex items-center gap-2">
              <input data-testid="statut-form-color" type="color" value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })} className="h-9 w-14 cursor-pointer rounded border border-slate-200" />
              <input value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })} className="pf-input mono flex-1" />
            </div>
          </FieldRow>
          <FieldRow label="Statut"><select value={form.actif ? "1" : "0"} onChange={(e) => setForm({ ...form, actif: e.target.value === "1" })} className="pf-input"><option value="1">Actif</option><option value="0">Inactif</option></select></FieldRow>
        </Modal>
      )}
    </div>
  );
}

/* ------------------------- Shared ------------------------- */
const Loading = () => <div className="flex h-64 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;
function FieldRow({ label, children }) {
  return <div><label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-slate-400">{label}</label>{children}</div>;
}
function Modal({ title, onClose, onSubmit, children, testid, submitId, submitLabel }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <form onSubmit={onSubmit} className="relative z-10 w-full max-w-md rounded-2xl bg-white p-6 shadow-xl" data-testid={testid}>
        <div className="mb-4 flex items-center justify-between"><h2 className="font-heading text-lg font-bold">{title}</h2><button type="button" onClick={onClose}><X className="h-5 w-5 text-slate-400" /></button></div>
        <div className="space-y-3">{children}</div>
        <div className="mt-5 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-semibold hover:bg-slate-50">Annuler</button>
          <button data-testid={submitId} className="rounded-lg bg-[#0A0A0A] px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800">{submitLabel}</button>
        </div>
        <style>{`.pf-input{width:100%;border:1px solid #e2e8f0;border-radius:.5rem;padding:.5rem .625rem;font-size:.875rem;outline:none}.pf-input:focus{border-color:#94a3b8}`}</style>
      </form>
    </div>
  );
}
